import { next } from '@ember/runloop';
import { service } from '@ember/service';
import { cached, tracked } from '@glimmer/tracking';

import type {
	APIData,
	CreateRecordProperties,
	DateRangeInterface,
	PopupData,
	RecordData,
	RequestError,
	Service,
	VisualizationForList,
} from 'webapp-types';
import {
	BillingFeature,
	BlockFontSize,
	BlockVisualization,
	ModelName,
	ProductIssueType,
	SeriesVisualization,
} from 'webapp/enums/enums';
import { TrackingEvent } from 'webapp/enums/tracking';
import { UserSettingsKey } from 'webapp/enums/user-settings';
import { uniq } from 'webapp/utils/common';

import BlockPopupAiSummaryAutocreateComponent from 'webapp/components/block/popup-ai-summary-autocreate';
import BlockPopupCreateFromMetricSetupComponent from 'webapp/components/block/popup-create-from-metric-setup';
import BlockPopupDuplicateComponent from 'webapp/components/block/popup-duplicate';
import BlockPopupPreviewComponent from 'webapp/components/block/popup-preview';
import BlockPopupResolveDataComponent from 'webapp/components/block/popup-resolve-data';

import Block from 'webapp/models-custom/block';
import BlockSetup from 'webapp/models-custom/block-setup';
import type { MetricSetupChanges } from 'webapp/models-custom/block-state';
import type Board from 'webapp/models-custom/board';
import type { BlockChangesPayload } from 'webapp/models-custom/board-state';
import type DataSelect from 'webapp/models-custom/data-select';
import DatawallGrid from 'webapp/models-custom/datawall-grid';
import DateRange from 'webapp/models-custom/date-range';
import { EXPIRE_WARNING } from 'webapp/models-custom/infolayer-item/base';
import type InfolayerItemGeneral from 'webapp/models-custom/infolayer-item/general';
import MetricLimitations from 'webapp/models-custom/metric-limitations';
import MetricSetup from 'webapp/models-custom/metric-setup';
import BlockRecord from 'webapp/models/block';
import type BoardRecord from 'webapp/models/board';
import DataSelectRecord from 'webapp/models/data-select';

import { Severity } from 'webapp/services/logger';
import ModelManagerForStoreService from 'webapp/services/model-manager/for-store';
import type { AsyncRequestBgEventHandlerFn } from 'webapp/services/request';

export type TrackingOriginTypeForDatablockExplore = 'databoard';
export type TrackingOriginTypeForDatablockDuplicate = 'datablock_explorer' | 'designer';

// a new block's record props, as the designer collects them (drop, click, metric select)
export type NewBlockData = CreateRecordProperties<BlockRecord> & {
	metricType?: string; // only for tracking
};

// we also want some setups here that arent visualizations as such
// e.g. combo chart or leaderboard
export const enum EmptyBlockSetup {
	NUMBER = 1,
	LINE_CHART = 4,
	BAR_CHART = 5,
	COMBO_CHART = 12,
	PIE = 10,
	TABLE = 9,
	LEADERBOARD = 16,
	PIPELINE = 7,
	FUNNEL = 6,
	HEATMAP = 15,
	PROGRESS = 8,
	RADIAL_PROGRESS = 11,
	GAUGE = 21,
	HORIZONTAL_BAR_CHART = 13,
	SPIDER = 19,
	BUBBLE = 17,
	DATA_STORY = 14,
	TEXT = 22,
	IMAGE = 23,
	AI_SUMMARY = 24,
}

interface VisualizationListItem extends VisualizationForList {
	labelLong?: string;
	isHidden?: boolean;
}

export default class ModelManagerBlocksService extends ModelManagerForStoreService<
	BlockRecord,
	Block
> {
	@service('request') declare requestService: Service.Request;
	@service('infolayer') declare infolayerService: Service.Infolayer;
	@service('popup') declare popupService: Service.Popup;
	@service('navigation') declare navigationService: Service.Navigation;
	@service('analytics') declare analyticsService: Service.Analytics;
	@service('account') declare accountService: Service.Account;
	@service('billing') declare billingService: Service.Billing;
	@service('model-manager/boards') declare mmBoardsService: Service.ModelManager.Boards;
	@service('model-manager/sources') declare mmSourcesService: Service.ModelManager.Sources;
	@service('flow/promotions')
	declare flowPromotionsService: Service.Flow.Promotions;
	@service('user-settings') declare userSettingsService: Service.UserSettings;
	@service('current-user') declare currentUserService: Service.CurrentUser;
	@service('model-manager/metrics') declare mmMetricsService: Service.ModelManager.Metrics;
	@service('logger') declare loggerService: Service.Logger;
	@service('issues-report') declare issuesReportService: Service.IssuesReport;
	@service('request-queue') declare requestQueueService: Service.RequestQueue;

	DEFAULT_NAME = 'Untitled';

	@cached
	get savedAndUnsavedGeneratingRecords() {
		return this.allRecords.filter((blockRecord) => {
			// if you're gonna show unsaved blocks, they better be generating
			return !blockRecord.isDeleted && (blockRecord.id || blockRecord.isGenerating);
		});
	}

	@cached
	get blockRecordsByBoard(): Record<string, BlockRecord[]> {
		const map: Record<string, BlockRecord[]> = {};
		this.savedAndUnsavedGeneratingRecords.forEach((blockRecord) => {
			const boardId = blockRecord.boardId;
			if (boardId) {
				if (!map[boardId]) {
					map[boardId] = [];
				}
				map[boardId].push(blockRecord);
			}
		});
		return map;
	}

	get visualizationList(): VisualizationListItem[] {
		return [
			{ id: BlockVisualization.NUMBER, label: 'Number', icon: 'Metrics' },
			{ id: BlockVisualization.LINE_CHART, label: 'Line', icon: 'ChartLine' },
			{ id: BlockVisualization.BAR_CHART, label: 'Bar', icon: 'ChartBar' },
			{ id: BlockVisualization.PIE, label: 'Pie', icon: 'ChartPie' },
			{ id: BlockVisualization.TABLE, label: 'Table', icon: 'Table' },
			{ id: BlockVisualization.PIPELINE, label: 'Pipeline', icon: 'ChartPipeline' },
			{ id: BlockVisualization.FUNNEL, label: 'Funnel', icon: 'ChartFunnel' },
			{ id: BlockVisualization.PROGRESS, label: 'Progress', icon: 'ChartProgress' },
			{
				id: BlockVisualization.RADIAL_PROGRESS,
				label: 'Radial',
				labelLong: 'Radial progress',
				icon: 'ChartRadial',
			},
			{ id: BlockVisualization.GAUGE, label: 'Gauge', icon: 'ChartGauge' },
			{ id: BlockVisualization.HEATMAP, label: 'Heatmap', icon: 'ChartHeatmap' },
			{
				id: BlockVisualization.HORIZONTAL_BAR_CHART,
				label: 'Horiz. Bar',
				labelLong: 'Horizontal bar',
				icon: 'ChartBarHorizontal',
			},
			{
				id: BlockVisualization.DATA_STORY,
				label: 'Data Story',
				labelLong: 'Data story',
				icon: 'DataStory',
			},
			{ id: BlockVisualization.BUBBLE, label: 'Bubble', icon: 'ChartBubble' },
			{ id: BlockVisualization.SPIDER, label: 'Spiderweb', icon: 'ChartSpider' },
			{ id: BlockVisualization.TEXT, label: 'Text', icon: 'ChartText' },
			{ id: BlockVisualization.IMAGE, label: 'Image', icon: 'Image' },
			{
				id: BlockVisualization.AI_SUMMARY,
				label: 'AI Summary',
				labelLong: 'AI summary',
				isHidden:
					this.navigationService.isDatawallApp && !this.currentUserService.isLoggedIn,
				icon: 'AiSummary',
			},
		];
	}

	get switchableVisualizationList() {
		return this.visualizationList.filter((visualization) => {
			return ![BlockVisualization.TEXT, BlockVisualization.IMAGE].includes(visualization.id);
		});
	}

	switchableVisualizationListForReports = this.switchableVisualizationList.filter(
		(visualization) => {
			return ![BlockVisualization.DATA_STORY].includes(visualization.id);
		},
	);

	switchableVisualizationListForQueryBuilder = this.switchableVisualizationList.filter(
		(visualization) => {
			return ![
				BlockVisualization.DATA_STORY,
				BlockVisualization.PROGRESS,
				BlockVisualization.RADIAL_PROGRESS,
				BlockVisualization.GAUGE,
				BlockVisualization.AI_SUMMARY,
			].includes(visualization.id);
		},
	);

	switchableVisualizationListForDataCalculations =
		this.switchableVisualizationListForQueryBuilder;

	switchableVisualizationListForMetricDetails = this.switchableVisualizationList.filter(
		(visualization) => {
			return [
				BlockVisualization.BAR_CHART,
				BlockVisualization.LINE_CHART,
				BlockVisualization.PIE,
				BlockVisualization.HORIZONTAL_BAR_CHART,
				BlockVisualization.BUBBLE,
			].includes(visualization.id);
		},
	);

	isVisualizationSeries(visualization: BlockVisualization) {
		return [
			BlockVisualization.LINE_CHART,
			BlockVisualization.BAR_CHART,
			BlockVisualization.HEATMAP,
		].includes(visualization);
	}

	getVisualizationNameFromVisualizationType(visualizationType: BlockVisualization): string {
		return (
			this.visualizationList.find((item) => item.id === visualizationType)?.label || 'Unknown'
		);
	}

	getVisualizationTypeIconPath(type: number, size = '') {
		// return `/images/visualizations/c2/${size ? size + '/' : ''}type-${type}.svg`;
		return `https://cdn1.databox.com/images/visualizations/c2/${size ? size + '/' : ''}type-${type}.svg`;
	}

	getVisualizationTypeIcon(type: number): IconName | undefined {
		return this.visualizationList.find((item) => item.id === type)?.icon;
	}

	private _blocksRequestingPromises: { id: string; promise: Promise<BlockRecord | null> }[] = [];
	private _blocksDataRequestingPromises: { id: string; promise: Promise<unknown> }[] = [];

	requestSingleBlock(blockId: string, ignoreCache = false): Promise<BlockRecord | null> {
		// used for report slides only !
		// returns block, childblocks and metricsettings
		let promise: Promise<BlockRecord | null>;

		// also make sure data for block is requested
		const getBlockData = (blockId: string) => {
			let dataPromise;
			const sampleRecord = ignoreCache
				? null
				: this.storeService.peekRecord(ModelName.SAMPLE, blockId);
			if (sampleRecord) {
				dataPromise = Promise.resolve();
			} else {
				dataPromise = ignoreCache
					? undefined
					: this._blocksDataRequestingPromises.find((item) => item.id === blockId)
							?.promise;
				if (!dataPromise) {
					dataPromise = this.requestService
						.api<RecordData.Payload>('GET', `/blocks/${blockId}/data`)
						.then((payload) => {
							this.storeService.pushPayload(payload);
						})
						.finally(() => {
							this._blocksDataRequestingPromises =
								this._blocksDataRequestingPromises.filter((p) => p.id !== blockId);
						})
						.catch(
							this.requestService.handleGenericThrownError.bind(this.requestService),
						);

					this._blocksDataRequestingPromises.push({
						id: blockId,
						promise: dataPromise,
					});
				}
			}
			return dataPromise;
		};

		// first check if already requested
		const blockRecord = this.findRecord(blockId);
		if (blockRecord) {
			return getBlockData(blockId)
				.then(() => blockRecord)
				.catch(
					this.requestService.handleGenericThrownError.bind(this.requestService),
				) as Promise<BlockRecord | null>;
		} else {
			const alreadyExistingPromise = this._blocksRequestingPromises.find(
				(item) => item.id === blockId,
			)?.promise;

			if (alreadyExistingPromise) {
				promise = alreadyExistingPromise;
			} else {
				promise = new Promise((resolve) => {
					this.requestService
						.api<RecordData.Payload>('GET', `/blocks/${blockId}`)
						.then((reply) => {
							next(() => {
								this.storeService.pushPayload(reply);

								// also request data
								void getBlockData(blockId);

								const block = this.findRecord(blockId)!;
								resolve(block);
							});
						})
						.finally(() => {
							this._blocksRequestingPromises = this._blocksRequestingPromises.filter(
								(p) => p.id !== blockId,
							);
						})
						.catch(
							this.requestService.handleGenericThrownError.bind(this.requestService),
						);
				});

				this._blocksRequestingPromises.push({
					id: blockId,
					promise,
				});
			}
		}

		return promise;
	}

	updateBlockMasterMetricSetup(block: Block, metricSetupChanges?: MetricSetupChanges) {
		return this.requestService
			.api<RecordData.Payload>('PUT', `/blocks/${block.id}/metric-setup`, metricSetupChanges)
			.then((payload) => {
				this.storeService.pushPayload(payload);
			})
			.catch(this.requestService.showAllValidationErrors.bind(this.requestService));
	}

	updateBlocksOnBoardStateChange(blockChanges: BlockChangesPayload[]): void {
		blockChanges.forEach((blockData) => {
			if (!blockData.changes) {
				return;
			}

			const block = this.findModel(String(blockData.id));

			if (!block) {
				return;
			}

			const blockSetup = block.blockSetup;

			if (!blockSetup) {
				return;
			}

			let promiseChain: Promise<unknown> = Promise.resolve();

			const newDateRange = blockData.changes.dateRange;
			if (newDateRange) {
				const afterLimitationsLoad = blockSetup.limitations.limitationsLoaded;
				if (afterLimitationsLoad) {
					promiseChain = afterLimitationsLoad.then(() => {
						// the given dateRange has to be set on the primary data-selecs of the given block
						const dateRange = DateRange.createFromFormula(newDateRange);
						blockSetup.changeFullDateRange(dateRange);
					});
				}
			}

			// dateRange is handled first, so the batchChangeDateRange will be called
			// but skip saving block from here onwards bcos we might be changing multiple props
			promiseChain = promiseChain.then(() => {
				blockSetup.isAutosaveOn = false;
			});

			const tableColumnType = blockData.changes.settings?.tableColumnType;
			if (tableColumnType !== undefined) {
				promiseChain = promiseChain.then(() => {
					block.tableColumnType = tableColumnType;
				});
			}

			promiseChain = promiseChain.then(() => {
				blockSetup.isAutosaveOn = true; // set it back before calling save
				return blockSetup.saveEverything();
			});

			const metricSetupChanges = blockData.changes.metricSetup;
			if (metricSetupChanges) {
				void promiseChain.then(() => {
					this.addBlockIdToRequestedSamples(block.id!);

					// custom request
					void this.updateBlockMasterMetricSetup(block, metricSetupChanges);
				});
			}
		});
	}

	getModifierFromFontSize(fontSize: BlockFontSize): number {
		const coefficients = {
			[BlockFontSize.SMALL]: 0.8,
			[BlockFontSize.MEDIUM]: 1,
			[BlockFontSize.LARGE]: 1.3,
		};
		return coefficients[fontSize] || 1;
	}

	bulkCreate(
		blocksData: RecordData.Block.Serialized[],
		board: Board,
		showLayer = true,
		isFromOnboarding = false,
		bgEventHandler?: AsyncRequestBgEventHandlerFn,
	): Promise<void> {
		const layer = showLayer ? this.infolayerService.loading('Creating datablocks...') : null;

		if (!board?.canCurrentUserEdit) {
			if (layer) {
				layer.error("You can't add blocks to board");
			}
			return Promise.reject();
		}

		const requestId = this.requestService.generateRequestId();

		const batchRequest: APIData.Block.BatchRequest = { blocks: blocksData, isFromOnboarding };
		let promise: Promise<unknown> = this.requestService.apiWithAsyncReply(
			'POST',
			'/blocks/batch',
			batchRequest,
			{ requestId },
			bgEventHandler,
		);

		if (isFromOnboarding) {
			// if the async request takes longer than 30 seconds, we log an error and resolve the promise
			const timeoutPromise = new Promise<void>((resolve) => {
				setTimeout(() => {
					this.issuesReportService.triggerEventIssue({
						type: ProductIssueType.GENERAL_TIMEOUT,
						page: window.location.pathname,
						url: window.location.href,
						errorMessage: 'timeout_onboarding',
						requestId,
					});
					this.loggerService.logError(
						'Onboarding: Block creation timed out after 30 seconds',
						undefined,
						Severity.WARNING,
					);
					resolve();
				}, 30000);
			});
			promise = Promise.race([timeoutPromise, promise]);
		}

		return promise
			.then(() => {
				if (layer) {
					layer.ok('Datablocks created');
				}
			})
			.catch((err: RequestError) => {
				if (layer) {
					layer.close();
				}
				this.requestService.showAllValidationErrors(err);
				throw err;
			});
	}

	batchChangeDateRange(block: Block, dateRange: DateRange): Promise<unknown> {
		return this.requestService
			.api<APIData.Block.RecordResponse>(
				'PUT',
				`/blocks/${block.id}/date-range`,
				dateRange.formula,
			)
			.then((reply) => {
				// ignore sent data-selects bcos we already manually updated them in updateDataSelectsFromPrimaryDateRange
				// this way we avoid race-conditions when we save multiple things at the same time and BE overrides stuff
				if (reply.block?.id) {
					this.storeService.pushPayload({
						block: { id: reply.block.id, dateRange: reply.block.dateRange },
					});
				}
			})
			.catch(this.requestService.showAllValidationErrors.bind(this.requestService));
	}

	promptDeleteFromBoard(block: Block, prevBlock?: Block): void {
		const affirmativeFunc = () => {
			block
				.delete()
				.catch(this.requestService.handleGenericThrownError.bind(this.requestService));
			this.infolayerService.ok('Block deleted!');

			if (prevBlock) {
				this.navigationService.transitionToRoute(
					'designer.board.block',
					block.board,
					prevBlock.model,
				);
			} else {
				this.navigationService.transitionToRoute('designer.board', block.board);
			}
		};

		this.popupService.openConfirmPopup({
			onConfirm: affirmativeFunc,
			title: 'Are you sure you want to delete this block?',
			text: '<strong>This cannot be undone.</strong>',
			confirmAction: 'Delete',
			cancelAction: 'Cancel',
		});
	}

	openDebugPopup(blockId: number) {
		return this.navigationService.openDebugPopup(blockId);
	}

	getBlockDebugInfo(blockId: number): Promise<APIData.PlatformAdmin.MetricsInfo | null> {
		return this.requestService
			.api<APIData.PlatformAdmin.MetricsInfo[]>('POST', '/platform-admin/metrics-info', [
				blockId,
			])
			.then((reply) => {
				// reply = window.dbx_mock['POST /d/platform-admin/metrics-info'];
				if (reply?.[0]) {
					return reply[0];
				} else {
					return null;
				}
			})
			.catch(
				this.requestService.showAllValidationErrors.bind(this.requestService),
			) as Promise<APIData.PlatformAdmin.MetricsInfo | null>;
	}

	duplicateTo(sourceBlock: Block, blockName: string, boardId: string) {
		const layer = this.infolayerService.loading('Duplicating datablock...'),
			boardRecord = this.storeService.peekRecord('board', boardId) as BoardRecord;

		if (!boardRecord) {
			return Promise.reject();
		}

		const board = this.mmBoardsService.createModelFromRecord(boardRecord),
			params: APIData.Block.CloneRequest = {
				block: {
					boardId: boardId,
					name: blockName,
					order: board.blocks.length,
				},
			};

		let boardDataPromise: Promise<unknown>;
		if (board.blockObjs.length) {
			boardDataPromise = Promise.resolve();
		} else {
			boardDataPromise = this.mmBoardsService.requestBoardData(boardId);
		}

		return boardDataPromise.then(() => {
			const savePromises: Promise<unknown>[] = [];

			const wallObj = new DatawallGrid();
			wallObj.initGridWithoutDOM(board.blockObjs);
			const found = wallObj.findSpaceWithFallbackResize(
				sourceBlock.posX,
				sourceBlock.posY,
				sourceBlock.width,
				sourceBlock.height,
			);
			if (found.freeSpace) {
				params.block.settings = {
					position: Object.assign(
						{
							m: true,
						},
						found.freeSpace,
					),
				};
				found.blocksResized.forEach((node) => {
					const resizedBlock = board.blockObjs.find((item) => item.localId === +node.id);
					if (resizedBlock) {
						resizedBlock.posX = node.x;
						resizedBlock.posY = node.y;
						resizedBlock.width = node.width;
						resizedBlock.height = node.height;
						savePromises.push(resizedBlock.saveAndCatch());
					}
				});
			}

			return Promise.allSettled(savePromises).then(() => {
				return this.requestService
					.api<APIData.Block.RecordResponse>(
						'POST',
						`/blocks/${sourceBlock.id}/clone`,
						params,
					)
					.then((reply) => {
						layer.ok(`Datablock <strong>${sourceBlock.name}</strong> duplicated!`);
						this.storeService.pushPayload(reply);

						const blockModel = this.findModel(String(reply.block.id));

						return blockModel;
					})
					.catch(this.requestService.showAllValidationErrors.bind(this.requestService));
			});
		});
	}

	openCreateFromMetricSetupPopup(data: PopupData.Block.CreateFromMetricSetup) {
		const popup = this.popupService.createModal(BlockPopupCreateFromMetricSetupComponent, {
			popupClass: 'popup-type-create-block',
			allowStacked: true, // for blocktype-picker
		});

		popup.open(data);

		return popup;
	}

	openResolveDataPopup(model: Block) {
		return this.popupService
			.createModal(BlockPopupResolveDataComponent, {
				popupClass: 'popup-type-board-resolve-data',
				allowStacked: true, // source/connect/popup
			})
			.open({
				model,
			});
	}

	private _preventAddBlockToBoardInDesigner = false;

	addBlockToBoardInDesigner(
		board: Board,
		blockData: NewBlockData = {},
		blockSetupId?: EmptyBlockSetup,
		activeDatawall?: DatawallGrid,
	): Promise<BlockRecord> {
		// from designer left panel - either by drag or click

		// don't allow to add more than one block to before it's saved
		// we assume that in designer, we can't be adding to more than one board at a time, so single flag
		if (this._preventAddBlockToBoardInDesigner) {
			return Promise.reject({
				code: 422, // so the showAllValidationErrors grabs it
				errors: [
					{
						field: 'general',
						message: 'Adding block already in progress',
					},
				],
			});
		} else {
			this._preventAddBlockToBoardInDesigner = true;
		}

		const isFromMetric = !!blockData.fromMetricSetup?.key,
			isFromEmpty = !isFromMetric;

		const metricSetup = isFromMetric ? new MetricSetup(blockData.fromMetricSetup) : null;

		if (!blockData.visualization) {
			delete blockData.visualization;
		}

		let visualization: BlockVisualization = BlockVisualization.NUMBER;
		let visualizationToTrack = '';

		// blockSetup is EmptyBlockSetup

		// handle exceptions first
		if (blockSetupId === EmptyBlockSetup.COMBO_CHART) {
			visualization = BlockVisualization.BAR_CHART;
			visualizationToTrack = 'Combo';
		} else if (blockSetupId === EmptyBlockSetup.LEADERBOARD) {
			visualization = BlockVisualization.TABLE;
			visualizationToTrack = 'Leaderboard';
		} else if (blockSetupId) {
			// the other setups have their visualization's value
			const setupValue: number = blockSetupId;
			visualization = setupValue;
		} else if (blockData.visualization) {
			visualization = blockData.visualization;
		} else {
			delete blockData.visualization; // to not overwrite with null / undefined
		}

		if (!blockData.fromMetricDateRange && !this.isVisualizationSeries(visualization)) {
			// granulation only, the range comes from the metric
			blockData.fromMetricDateRange = {
				granulation: { isWholeRange: true },
			} as DateRangeInterface.FormulaInterface;
		}

		if (blockData.fromMetricDateRange?.range) {
			const dateRange = this.getValidCommonDateRange(
				DateRange.createFromFormula(blockData.fromMetricDateRange),
				visualization,
			);

			blockData.fromMetricDateRange = dateRange?.formula;
		}

		if (isFromMetric && !blockData.name) {
			blockData.name = metricSetup?.selectedMetricTitle;
		}

		const data = Object.assign(
			{
				isGenerating: true,
				boardId: board.numericId!,
				order: board.blocks.filter((item) => item.id).length, // put on the last spot
				visualization,
			},
			blockData,
		);

		const block = this.createNewModel(data);

		let firstDs: DataSelect | null = null,
			secondDs: DataSelect | null = null;

		if (isFromEmpty && block instanceof Block) {
			const blockSetup = block.blockSetup;

			blockSetup.isAutosaveOn = false;

			if (blockSetup.visualization.recordLimit) {
				const dsData: { dateRange?: DateRangeInterface.FormulaInterface } = {};
				if (blockData.fromMetricDateRange?.range) {
					dsData.dateRange = blockData.fromMetricDateRange;
				}

				// no DS for image / text
				firstDs = blockSetup.createPrimaryMainDataSelect(dsData);
			}

			switch (blockSetupId) {
				case EmptyBlockSetup.COMBO_CHART:
					secondDs = blockSetup.createPrimaryMainDataSelect({
						visualization: SeriesVisualization.LINE,
					});
					break;
				case EmptyBlockSetup.LEADERBOARD:
					block.hasRowNumbers = true;
					break;
			}
		}

		this.checkToMarkBlockVisualizationUsed(block);

		// ----> tracking start
		const trackData: {
			page: string;
			origin: string;
			type: APIData.Tracking.Value;
			visualization: string;
			metrics: string[];
			datasource?: string;
		} = {
			page: 'designer',
			origin: isFromMetric ? 'metrickey_designer' : 'empty',
			type: data.metricType,
			visualization: visualizationToTrack || block.typeName,
			metrics: [],
		};

		if (isFromMetric) {
			trackData.datasource = metricSetup?.metric?.dataSourceTypeKey;

			const metricName = metricSetup?.metric?.name;
			if (metricName) {
				trackData.metrics = [metricName];
			}
		}

		this.analyticsService.event(TrackingEvent.DATABLOCK_DROP, trackData);

		// <---- tracking end

		const blockCreateCallback = (boardId: number) => {
			block.boardId = boardId;

			return block
				.save()
				.then(
					(record: BlockRecord) => {
						block.directPropUpdate({ isGenerating: false });

						if (activeDatawall) {
							// resets noMove and noResize
							activeDatawall.updateBlockMoveAndResize(
								String(record.localId),
								false,
								false,
							);
						}

						// possibly create dataselects
						const dsPromises: Promise<unknown>[] = [];
						const blockId = +record.id!;
						if (firstDs) {
							firstDs.blockId = blockId;
							dsPromises.push(firstDs.saveAndCatch());
						}
						if (secondDs) {
							secondDs.blockId = blockId;
							dsPromises.push(secondDs.saveAndCatch());
						}

						return Promise.all(dsPromises).then(() => {
							const blockSetup = block.blockSetup;
							if (firstDs) {
								// add compare line, if supported
								blockSetup.checkToAddAdditionalLines(firstDs);
							}
							return blockSetup.doAfterNewBlockCreatedActivities().then(() => {
								return record;
							});
						});
					},
					(errorData: RequestError) => {
						block
							.delete()
							.catch(
								this.requestService.handleGenericThrownError.bind(
									this.requestService,
								),
							);
						throw errorData;
					},
				)
				.finally(() => {
					this._preventAddBlockToBoardInDesigner = false;
				});
		};

		if (board.numericId) {
			return blockCreateCallback(board.numericId);
		} else {
			return board
				.save()
				.then(
					(boardRecord: BoardRecord) => {
						this.mmBoardsService.triggerDataboardCreated(
							{ from: 'designer', datasources: [trackData.datasource] },
							board,
						);

						// new board, mark it so we don't request 'data' again
						void this.mmBoardsService.markBoardRequestedAndSubscribeToDataChannel(
							boardRecord.id!,
						);

						return +boardRecord.id!;
					},
					(errorData) => {
						this._preventAddBlockToBoardInDesigner = false;
						throw errorData;
					},
				)
				.then(blockCreateCallback);
		}
	}

	getRecordLimitForVisualizationType(visualizationType: BlockVisualization) {
		return BlockSetup.getRecordLimitForVisualizationType(visualizationType);
	}

	getValidCommonDateRange(
		commonDateRange?: DateRange | null,
		visualization: BlockVisualization = BlockVisualization.NUMBER,
	): DateRange | null {
		if (!commonDateRange) {
			return null;
		}

		const limitations = new MetricLimitations({
			isNumber: !this.isVisualizationSeries(visualization),
			dateRange: commonDateRange,
		});
		limitations.selectOptimalValidGranulation();

		return limitations.dateRange;
	}

	checkToMarkBlockVisualizationUsed(block: Block, skipVisualizationCheck = false) {
		// ai summary used -- marked on create new, change existing vis, duplicating vis
		if (
			this.currentUserService.isLoggedIn &&
			!this.userSettingsService.hasUsedAiSummaryBlock &&
			block instanceof Block &&
			(block.blockSetup.visualization.isAiSummary || skipVisualizationCheck)
		) {
			void this.userSettingsService.save(UserSettingsKey.AI_SUMMARY_BLOCK_USED, true);
		}
	}

	checkToOpenAiSummaryAutocreatePopup(board: Board, activeDatawall?: DatawallGrid) {
		const isPopupEligible =
			!this.popupService.anyPopupsOpen &&
			!this.userSettingsService.hasSeenAiSummaryAutocreatePopup;
		if (!isPopupEligible) {
			return;
		}

		const isUserEligible =
			!this.userSettingsService.hasUsedAiSummaryBlock &&
			!this.currentUserService.hasSpecialPermissions;
		if (!isUserEligible) {
			return;
		}

		if (this.mmBoardsService.normalBoardModels.length < 3) {
			return;
		}

		const metricsUsed = board.blockObjs.reduce((count, b) => {
			return count + b.blockSetup.dataSelectsShown.filter((ds) => ds.metricSetup.key).length;
		}, 0);

		const isAiSummaryBlockUsed = board.blockObjs.find(
			(b: Block) => b.visualization === BlockVisualization.AI_SUMMARY,
		);

		const isBoardEligible =
			board.blockObjs.length < 15 && !isAiSummaryBlockUsed && metricsUsed > 3;
		if (!isBoardEligible) {
			return;
		}
		const nextPlan = this.billingService.getNextPlanForFeature(BillingFeature.GENERATIVE_AI);
		const isAccountEligible =
			this.billingService.generativeAIEnabled ||
			this.billingService.canRequestTrial ||
			(!!nextPlan &&
				this.currentUserService.isAdmin &&
				!!this.flowPromotionsService.getAvailableTrialPromotion(nextPlan));
		if (!isAccountEligible) {
			return;
		}

		const popup = this.popupService.createModal(BlockPopupAiSummaryAutocreateComponent, {
			popupClass: 'popup-type-block-ai-summary-autocreate',
			showClose: false,
		});

		popup.open({
			board,
			activeDatawall,
		});

		return popup;
	}

	openPreviewPopup(props: PopupData.Block.Preview, elementToAnimateFrom?: HTMLElement) {
		const popup = this.popupService.createModal(BlockPopupPreviewComponent, {
			popupClass: 'popup-type-block-preview popup--fullscreen',
			allowStacked: true,
			elementToAnimateFrom,
		});

		popup.open(props);

		return popup;
	}

	openDuplicatePopup(props: PopupData.Block.Duplicate) {
		const popup = this.popupService.createModal(BlockPopupDuplicateComponent, {
			popupClass: 'popup-type-duplicate-block title-bigger',
		});

		popup.open(props);

		return popup;
	}

	// ------ "samples loading" logic on ds / block edit (for designer) ------

	@tracked private _requestedSamplesForBlockIds: string[] = [];

	private get requestedSamplesForBlockIds() {
		let currentBlockIds: string[] = [];
		const current = this.requestQueueService.current[0];

		if (current) {
			if (current instanceof BlockRecord && current.id) {
				currentBlockIds = [current.id];
			}

			if (current instanceof DataSelectRecord && current.blockId) {
				currentBlockIds = [String(current.blockId)];
			}
		}

		return uniq([...currentBlockIds, ...this._requestedSamplesForBlockIds]);
	}

	addBlockIdToRequestedSamples(blockId: string) {
		if (!this.navigationService.isDesigner) {
			return;
		}

		this.startSamplesLoadingTimeout(blockId);
		this._requestedSamplesForBlockIds = [...this._requestedSamplesForBlockIds, blockId];
	}

	removeBlockIdFromRequestedSamples(blockId: string) {
		if (!this.navigationService.isDesigner) {
			return;
		}

		this.clearSamplesLoadingTimeout(blockId);

		this._requestedSamplesForBlockIds = this._requestedSamplesForBlockIds.filter((id) => {
			return id !== blockId;
		});
	}

	areSamplesForBlockLoading(blockId: string | null) {
		if (!blockId) {
			return false;
		}

		return this.requestedSamplesForBlockIds.includes(blockId);
	}

	@tracked private samplesLoadingInfolayerItemInDesigner: InfolayerItemGeneral | null = null;
	private samplesLoadingTimeouts: Record<string, ReturnType<typeof setTimeout>> = {};

	private startSamplesLoadingTimeout(blockId: string): void {
		this.clearSamplesLoadingTimeout(blockId);

		const timeoutDuration = 30 * 1000;
		this.samplesLoadingTimeouts[blockId] = setTimeout(() => {
			this.issuesReportService.triggerEventIssue({
				type: ProductIssueType.DATA_LOADING_DELAY,
				page: window.location.pathname,
				url: window.location.href,
				errorMessage: `Samples for block {block_id} did not load after ${timeoutDuration / 1000} seconds`,
				endpoint: 'pusher',
				errorItemId: blockId,
				duration: timeoutDuration,
			});

			if (!this.samplesLoadingInfolayerItemInDesigner) {
				this.samplesLoadingInfolayerItemInDesigner = this.infolayerService.warning(
					`Looks like fetching data is taking longer than expected.`,
				);

				setTimeout(() => {
					this.samplesLoadingInfolayerItemInDesigner = null;
				}, EXPIRE_WARNING);

				this._requestedSamplesForBlockIds = [];
			}
		}, timeoutDuration);
	}

	private clearSamplesLoadingTimeout(blockId: string): void {
		const timeout = this.samplesLoadingTimeouts[blockId];

		if (timeout) {
			clearTimeout(timeout);
			delete this.samplesLoadingTimeouts[blockId];
		}
	}

	// ------ For-store ------

	modelName = ModelName.BLOCK;

	override createNewRecord(properties: CreateRecordProperties<BlockRecord> = {}): BlockRecord {
		if (typeof properties.name === 'undefined') {
			properties.name = this.DEFAULT_NAME;
		}

		return super.createNewRecord(properties);
	}

	override createNewModel(properties: CreateRecordProperties<BlockRecord> = {}): Block {
		const block = super.createNewModel(properties);
		if (block instanceof Block) {
			block.directPropUpdate({
				recordLimit: block.blockSetup.visualization.recordLimit,
			});
		}
		return block;
	}

	createModel(model: BlockRecord): Block {
		return new Block({ model });
	}
}

declare module '@ember/service' {
	interface Registry {
		'model-manager/blocks': ModelManagerBlocksService;
	}
}
